import _ from "lodash";
import logger from "../../utils/LoggerFrontend";
import React, { useEffect, useMemo, useState } from "react";
import {
  formatIntlNumber,
  getSumByChannelAndMetricField,
  getSumByChannelAndMetricFieldFloat
} from "../../utils/Helpers";
import PeopleMetricIcon from "./Campaign__Metrics-Icons/People__Metric-Icon";
import webAPIUtils from "../../utils/WebAPIUtils.js";
import { useTranslation } from "react-i18next";
import EyeMetricIcon from "./Campaign__Metrics-Icons/Eye__Metric-Icon";
import MouseMetricIcon from "./Campaign__Metrics-Icons/Mouse__Metric-Icon";
import PlantMetricIcon from "./Campaign__Metrics-Icons/Plant__Metric-Icon";
import ThumbsUpMetricIcon from "./Campaign__Metrics-Icons/ThumbsUp__Metric-Icon";
import TextMetricIcon from "./Campaign__Metrics-Icons/Text__Metric-Icon";

const calculateCTR = (channel, metrics) => {
  const totalClicks = getSumByChannelAndMetricField(metrics, channel, "totalClicks");
  const totalImpressions = getSumByChannelAndMetricField(metrics, channel, "totalImpressions");

  if (!totalImpressions) return 0;

  return ((totalClicks / totalImpressions) * 100).toFixed(2);
};

const CampaignReadStats = ({
  channels = [],
  isVideoCampaign,
  socialCommentsCount,
  selectedAdsetId,
  channelCampaigns,
  campaignMetrics,
  campaignData,
  language
}) => {
  const { t } = useTranslation();

  const [campaignId, setCampaignId] = useState("");
  const [EBMetrics, setEBMetrics] = useState({});

  logger.debug("CampaignReadStats", "called", {
    channels,
    socialCommentsCount,
    selectedAdsetId,
    channelCampaigns,
    campaignMetrics,
    campaignData
  });
  logger.debug("CampaignReadStats", "Metrics objects", { campaignMetrics, channelCampaigns });

  const metrics = useMemo(() => {
    if (!channelCampaigns || !channelCampaigns.length) {
      return campaignMetrics;
    }

    const result = {};

    channelCampaigns.map((channelCampaign, index) => {
      if (!selectedAdsetId || channelCampaign.adsetId == selectedAdsetId) {
        result[
          channelCampaign.channel +
            "__" +
            channelCampaign.platformCampaignId +
            "__" +
            channelCampaign.platformAdsetId
        ] = channelCampaign.metrics || {};
      }
      if (!campaignId) {
        setCampaignId(channelCampaign.campaignId);
      }
    });
    logger.debug("CampaignReadStats", "calculated metrics", { result });
    return result;
  }, [channelCampaigns, selectedAdsetId, campaignMetrics]);

  useEffect(() => {
    const getData = async () => {
      const metrics = await webAPIUtils.getEmployerBrandingMetrics(campaignId, selectedAdsetId);
      setEBMetrics(metrics);
    };

    getData();
  }, [campaignId, selectedAdsetId]);

  const _renderStatsByChannel = (channelName, labelLikes = "Likes") => {
    return (
      <div className="campaign-read__social campaign-read__facebook">
        <div className="campaign-read__social-container">
          <ul>
            <li className="center">
              <EyeMetricIcon />
              <h3>{t("Impressions")}</h3>
              <span>
                {formatIntlNumber(
                  getSumByChannelAndMetricField(metrics, channelName, "totalImpressions"),
                  { language }
                )}
              </span>
            </li>
            <li className="center">
              <MouseMetricIcon />
              <h3>{t("Clicks")}</h3>
              <span>
                {formatIntlNumber(
                  getSumByChannelAndMetricField(metrics, channelName, "totalClicks"),
                  { language }
                )}
              </span>
            </li>
            <li className="center">
              <PeopleMetricIcon />
              <h3>CTR</h3>
              <span>
                {formatIntlNumber(calculateCTR(channelName, metrics), {
                  language,
                  points: 2
                })}
                %
              </span>
            </li>
            <li className="center">
              <TextMetricIcon />
              <h3>{t("Comments")}</h3>
              <span>
                {formatIntlNumber(
                  getSumByChannelAndMetricField(metrics, channelName, "totalComments"),
                  { language }
                )}
              </span>
            </li>
            <li className="center">
              <ThumbsUpMetricIcon />
              <h3>{labelLikes}</h3>
              <span>
                {formatIntlNumber(
                  getSumByChannelAndMetricField(metrics, channelName, "totalLikes"),
                  { language }
                )}
              </span>
            </li>
            <li className="center">
              <PlantMetricIcon />
              <h3>Shares</h3>
              {formatIntlNumber(
                getSumByChannelAndMetricField(metrics, channelName, "totalShares"),
                { language }
              )}
            </li>

            {isVideoCampaign && (
              <li className="center">
                <MouseMetricIcon />
                <h3>{t("All Clicks")}</h3>
                <span>
                  {formatIntlNumber(
                    getSumByChannelAndMetricField(metrics, channelName, "allClicks"),
                    { language }
                  )}
                </span>
              </li>
            )}
            {isVideoCampaign && (
              <li className="center">
                <svg width="25" height="21" viewBox="0 0 25 21" xmlns="http://www.w3.org/2000/svg">
                  <g fillRule="nonzero" fill="none">
                    <path
                      d="M14.5715 7.217C17.125353 8.4080376 19.3366854 10.2253308 21 12.5c-.7608 1.3994-5.7234 6.5-11.4438 6.5-.7042382-.0021261-1.40648707-.0748904-2.0962-.2172 1.568347.7761235 3.2903016 1.1919892 5.04 1.2172 5.72 0 10.683-5.1006 11.4438-6.5-2.2965446-3.1224421-5.6115253-5.34473797-9.3723-6.283z"
                      className="icon icon__shade"
                    />
                    <path
                      d="M12.5 6C5.5964 6 0 12.6211 0 13.5c0 .8789 5.5964 7.5 12.5 7.5S25 14.4375 25 13.5 19.4036 6 12.5 6zm0 14c-5.9141 0-10.6543-5.2236-11.429-6.5C1.8457 12.2236 6.5859 7 12.5 7c5.72 0 10.683 5.1011 11.4438 6.5C23.183 14.8994 18.22 20 12.5 20z"
                      className="icon__line"
                    />
                    <path
                      d="M12.5 10C10.5670034 10 9 11.5670034 9 13.5s1.5670034 3.5 3.5 3.5 3.5-1.5670034 3.5-3.5-1.5670034-3.5-3.5-3.5zm0 6c-1.3807119 0-2.5-1.1192881-2.5-2.5s1.1192881-2.5 2.5-2.5 2.5 1.1192881 2.5 2.5c-.0015981 1.3800494-1.1199506 2.4984019-2.5 2.5zM7.0671 5.4819c.08951188.15479166.25479053.25008608.4336.25.0878507.0002139.1741622-.02305568.25-.0674.11488718-.0661556.19874795-.1752856.23309502-.3033321.03434708-.12804648.01636033-.26449598-.04999502-.3792679l-1-1.7314c-.1380712-.23919452-.44390547-.32117117-.6831-.1831-.2391945.1380712-.32117118.44390548-.1831.6831l.9995 1.7314zM17.25 5.6646c.0758378.04434432.1621493.0676139.25.0674.1788095.00008608.3440881-.09520834.4336-.25l1-1.7314c.1380712-.23919452.0560945-.5450288-.1831-.68309997-.2391945-.13807118-.5450288-.05609454-.6831.18309997l-1 1.7314c-.0663968.11471126-.0844597.25111513-.0502084.37915442.0342514.12803928.1180061.2372063.2328084.30344558zM12.5 4c.2761424 0 .5-.22385763.5-.5v-3c0-.27614237-.2238576-.5-.5-.5s-.5.22385763-.5.5v3c0 .27614237.2238576.5.5.5z"
                      className="icon__line"
                    />
                  </g>
                </svg>
                <h3>Videowiedergabe</h3>
                <span>
                  {formatIntlNumber(
                    getSumByChannelAndMetricField(metrics, channelName, "totalVideoViews"),
                    { language }
                  )}
                </span>
              </li>
            )}
            {isVideoCampaign && (
              <li className="center">
                <svg width="24" height="25" viewBox="0 0 24 25" xmlns="http://www.w3.org/2000/svg">
                  <g fillRule="nonzero" fill="none">
                    <path
                      d="M21.264 8.0666C20.1934 10.526 18.001 13 15 13c-1.027478.0144523-2.040669-.2417252-2.9378-.7428C12.3585 13.0373 13.9149 14 16 14c3.5128 0 5.9231-3.39 6.7158-6.178-.4748388.12850008-.96107.21042058-1.4518.2446zM11.5 20c-.5068517.0031308-1.0094573.0926813-1.4862.2648C11.6427684 20.8346964 12.8026793 22.2855367 13 24h2.9724c-.2575514-2.2762103-2.181667-3.99709-4.4724-4zM7.6261 10.3833c-1.65514642-.1355722-3.1800764-.95107917-4.2118-2.2524-1.0227918-1.2680957-1.47431235-2.90335968-1.2471-4.5166.0262-.19.056-.3623.0853-.5374-.1926629-.1947929-.36849064-.4055453-.5256-.63-.28259063.84979082-.4802241 1.72550803-.59 2.6143-.22729407 1.6131041.22404002 3.248276 1.2466 4.5164 1.0317236 1.3013208 2.55665358 2.1168278 4.2118 2.2524.8646.0747 1.6591.125 2.3739.15-.0303213-.5066445-.09187156-1.0109358-.1843-1.51-.3715-.0235-.7558-.0518-1.1588-.0867z"
                      className="icon icon__shade"
                    />
                    <path
                      d="M16 15c4.9166 0 8-5.5833 8-9-1.0520001.84362217-2.3929203 1.24097574-3.7347 1.1067-1.4037 0-2.8876-.1992-4.0979-.1992-.391186-.00625208-.7820799.02472075-1.1674.0925-2.3817271.45582825-4.0781077 2.57630394-4 5v7.0251c-2.83150093.2577685-4.9995056 2.6316902-5 5.4749v.5h11v-.5c-.0004944-2.8432098-2.1684991-5.2171315-5-5.4749v-5.2927c1.1572492.8517143 2.5634211 1.2973301 4 1.2676zm-.8073-7.0188c.3218594-.05516344.6482053-.07983943.9747-.0737.5471 0 1.1692.0444 1.8278.0916.7407.053 1.5066.1077 2.27.1077.8259977.01666985 1.6504237-.07910837 2.4506-.2847C21.9231 10.61 19.5128 14 16 14c-2.32 0-4-1.1946-4-2-.068827-1.9424477 1.2850067-3.6465812 3.1927-4.0188zM15.9724 24H7.0276c.25353896-2.278644 2.17969403-4.0023674 4.4724-4.0023674 2.292706 0 4.218861 1.7237234 4.4724 4.0023674z"
                      className="icon icon__line"
                    />
                    <path
                      d="M10 13c0-9.9583-8-8.2083-8-13C.9641 1.0359.42 2.9365.1459 4.9246c-.2530854 1.90074555.28445723 3.82211338 1.48714316 5.3155766C2.8357291 11.7336398 4.59830043 12.6684921 6.5093 12.8265 7.7054 12.93 8.9172 13 10 13zM2.3835 9.5776C1.36074374 8.30948858.90925985 6.67422622 1.1365 5.061c.1097759-.88879197.30740937-1.76450918.59-2.6143.73051264.9147621 1.63374017 1.67698244 2.6583 2.2433 2.1173 1.365 4.302 2.7734 4.5845 7.2905-.7148-.0254-1.5093-.0757-2.3739-.15-1.65526162-.1356699-3.1802457-.9513675-4.2119-2.2529z"
                      className="icon icon__line"
                    />
                  </g>
                </svg>
                <h3>Avg. Frequenz</h3>
                {getSumByChannelAndMetricFieldFloat(metrics, channelName, "averageFrequency") || 0}
              </li>
            )}
          </ul>
        </div>
        <div className="campaign-read__social-preview"></div>
      </div>
    );
  };

  const renderFacebookStats = () => {
    return _renderStatsByChannel("facebook");
  };

  const renderInstagramStats = () => {
    logger.debug("CampaignRead:renderInstagramStats", "called...", metrics);
    return _renderStatsByChannel("instagram", "Hearts");
  };

  const renderLinkedInFeedAdMetrics = () => {
    const channel = "linkedin-feed";
    const totalImpressions = getSumByChannelAndMetricField(metrics, channel, "totalImpressions");
    const totalClicks = getSumByChannelAndMetricField(metrics, channel, "totalClicks");
    const totalLikes = getSumByChannelAndMetricField(metrics, channel, "totalLikes");
    const totalShares = getSumByChannelAndMetricField(metrics, channel, "totalShares");
    const totalComments = getSumByChannelAndMetricField(metrics, channel, "totalComments");

    var ctr;

    try {
      ctr = ((totalClicks / totalImpressions) * 100).toFixed(2);
    } catch (error) {
      ctr = 0;
    }

    logger.debug("CampaignRead:renderLinkedInFeedAdMetrics", "mapped...", {
      totalClicks,
      totalImpressions,
      ctrReal: totalClicks / totalImpressions,
      ctr
    });

    return (
      <div className="campaign-read__social-container">
        <ul>
          <li className="center">
            <EyeMetricIcon />
            <h3>{t("Impressions")}</h3>
            <span>{formatIntlNumber(totalImpressions, { language })}</span>
          </li>
          <li className="center">
            <MouseMetricIcon />
            <h3>{t("Clicks")}</h3>
            <span>{formatIntlNumber(totalClicks, { language })}</span>
          </li>
          <li className="center">
            <PeopleMetricIcon />
            <h3>CTR</h3>
            <span>{formatIntlNumber(ctr, { language, points: 2 })} %</span>
          </li>
          <li className="center">
            <TextMetricIcon />
            <h3>{t("Comments")}</h3>
            <span>{totalComments || 0}%</span>
          </li>
          <li className="center">
            <ThumbsUpMetricIcon />
            <h3>Reactions</h3>
            <span>{totalLikes || 0}</span>
          </li>
          <li className="center">
            <PlantMetricIcon />
            <h3>Shares</h3>
            <span>{totalShares || 0}</span>
          </li>
        </ul>
      </div>
    );
  };

  const renderPinterestFeedAdMetrics = () => {
    const channel = "pinterest";
    const totalImpressions = getSumByChannelAndMetricField(metrics, channel, "totalImpressions");
    const totalClicks = getSumByChannelAndMetricField(metrics, channel, "totalClicks");
    const totalLikes = getSumByChannelAndMetricField(metrics, channel, "totalLikes");
    const totalShares = getSumByChannelAndMetricField(metrics, channel, "totalShares");
    const totalComments = getSumByChannelAndMetricField(metrics, channel, "totalComments");

    var ctr;

    try {
      ctr = ((totalClicks / totalImpressions) * 100).toFixed(2);
    } catch (error) {
      ctr = 0;
    }

    logger.debug("CampaignRead:renderPinterestFeedAdMetrics", "mapped...", {
      totalClicks,
      totalImpressions,
      ctrReal: totalClicks / totalImpressions,
      ctr
    });

    return (
      <div className="campaign-read__social-container">
        <ul>
          <li className="center">
            <EyeMetricIcon />
            <h3>{t("Impressions")}</h3>
            <span>{formatIntlNumber(totalImpressions, { language })}</span>
          </li>
          <li className="center">
            <MouseMetricIcon />
            <h3>{t("Clicks")}</h3>
            <span>{formatIntlNumber(totalClicks, { language })}</span>
          </li>
          <li className="center">
            <PeopleMetricIcon />
            <h3>CTR</h3>
            <span>{formatIntlNumber(ctr, { language, points: 2 })} %</span>
          </li>
          <li className="center">
            <TextMetricIcon />
            <h3>{t("Comments")}</h3>
            <span>{totalComments || 0}%</span>
          </li>
          <li className="center">
            <ThumbsUpMetricIcon />
            <h3>Reactions</h3>
            <span>{totalLikes || 0}</span>
          </li>
          <li className="center">
            <PlantMetricIcon />
            <h3>Shares</h3>
            <span>{totalShares || 0}</span>
          </li>
        </ul>
      </div>
    );
  };

  const renderEbayKleinanzeigenFeedAdMetrics = () => {
    const channel = "ebay-kleinanzeigen";
    const totalImpressions = getSumByChannelAndMetricField(metrics, channel, "totalImpressions");
    const totalClicks = getSumByChannelAndMetricField(metrics, channel, "totalClicks");
    const totalLikes = getSumByChannelAndMetricField(metrics, channel, "totalLikes");
    const totalShares = getSumByChannelAndMetricField(metrics, channel, "totalShares");
    const totalComments = getSumByChannelAndMetricField(metrics, channel, "totalComments");

    var ctr;

    try {
      ctr = ((totalClicks / totalImpressions) * 100).toFixed(2);
    } catch (error) {
      ctr = 0;
    }

    logger.debug("CampaignRead:renderEbayKleinanzeigenFeedAdMetrics", "mapped...", {
      totalClicks,
      totalImpressions,
      ctrReal: totalClicks / totalImpressions,
      ctr
    });

    return (
      <div className="campaign-read__social-container">
        <ul>
          <li className="center">
            <EyeMetricIcon />
            <h3>{t("Impressions")}</h3>
            <span>{formatIntlNumber(totalImpressions, { language })}</span>
          </li>
          <li className="center">
            <MouseMetricIcon />
            <h3>{t("Clicks")}</h3>
            <span>{formatIntlNumber(totalClicks, { language })}</span>
          </li>
          <li className="center">
            <PeopleMetricIcon />
            <h3>CTR</h3>
            <span>{formatIntlNumber(ctr, { language, points: 2 })} %</span>
          </li>
          <li className="center">
            <TextMetricIcon />
            <h3>{t("Comments")}</h3>
            <span>{totalComments || 0}%</span>
          </li>
          <li className="center">
            <ThumbsUpMetricIcon />
            <h3>Reactions</h3>
            <span>{totalLikes || 0}</span>
          </li>
          <li className="center">
            <PlantMetricIcon />
            <h3>Shares</h3>
            <span>{totalShares || 0}</span>
          </li>
        </ul>
      </div>
    );
  };

  const renderRedditFeedAdMetrics = () => {
    const channel = "reddit";
    const totalImpressions = getSumByChannelAndMetricField(metrics, channel, "totalImpressions");
    const totalClicks = getSumByChannelAndMetricField(metrics, channel, "totalClicks");
    const totalLikes = getSumByChannelAndMetricField(metrics, channel, "totalLikes");
    const totalShares = getSumByChannelAndMetricField(metrics, channel, "totalShares");
    const totalComments = getSumByChannelAndMetricField(metrics, channel, "totalComments");

    var ctr;

    try {
      ctr = ((totalClicks / totalImpressions) * 100).toFixed(2);
    } catch (error) {
      ctr = 0;
    }

    logger.debug("CampaignRead:renderRedditFeedAdMetrics", "mapped...", {
      totalClicks,
      totalImpressions,
      ctrReal: totalClicks / totalImpressions,
      ctr
    });

    return (
      <div className="campaign-read__social-container">
        <ul>
          <li className="center">
            <EyeMetricIcon />
            <h3>{t("Impressions")}</h3>
            <span>{formatIntlNumber(totalImpressions, { language })}</span>
          </li>
          <li className="center">
            <MouseMetricIcon />
            <h3>{t("Clicks")}</h3>
            <span>{formatIntlNumber(totalClicks, { language })}</span>
          </li>
          <li className="center">
            <PeopleMetricIcon />
            <h3>CTR</h3>
            <span>{formatIntlNumber(ctr, { language, points: 2 })} %</span>
          </li>
          <li className="center">
            <TextMetricIcon />
            <h3>{t("Comments")}</h3>
            <span>{totalComments || 0}%</span>
          </li>
          <li className="center">
            <ThumbsUpMetricIcon />
            <h3>Reactions</h3>
            <span>{totalLikes || 0}</span>
          </li>
          <li className="center">
            <PlantMetricIcon />
            <h3>Shares</h3>
            <span>{totalShares || 0}</span>
          </li>
        </ul>
      </div>
    );
  };

  const renderSnapchatFeedAdMetrics = () => {
    const channel = "snapchat";
    const totalReach = getSumByChannelAndMetricField(metrics, channel, "totalReach");
    const totalImpressions = getSumByChannelAndMetricField(metrics, channel, "totalImpressions");
    const totalClicks = getSumByChannelAndMetricField(metrics, channel, "totalClicks");
    const totalLikes = getSumByChannelAndMetricField(metrics, channel, "totalLikes");
    const totalShares = getSumByChannelAndMetricField(metrics, channel, "totalShares");
    const totalComments = getSumByChannelAndMetricField(metrics, channel, "totalComments");

    var ctr;

    try {
      ctr = ((totalClicks / totalImpressions) * 100).toFixed(2);
    } catch (error) {
      ctr = 0;
    }

    logger.debug("CampaignRead:renderRedditFeedAdMetrics", "mapped...", {
      totalClicks,
      totalImpressions,
      ctrReal: totalClicks / totalImpressions,
      ctr
    });

    return (
      <div className="campaign-read__social-container">
        <ul>
          <li className="center">
            <PeopleMetricIcon />
            <h3>Reichweite</h3>
            <span>{formatIntlNumber(totalReach, { language })}</span>
          </li>
          <li className="center">
            <EyeMetricIcon />
            <h3>{t("Impressions")}</h3>
            <span>{formatIntlNumber(totalImpressions, { language })}</span>
          </li>
          <li className="center">
            <MouseMetricIcon />
            <h3>{t("Clicks")}</h3>
            <span>{formatIntlNumber(totalClicks, { language })}</span>
          </li>
          <li className="center">
            <PeopleMetricIcon />
            <h3>CTR</h3>
            <span>{formatIntlNumber(ctr, { language, points: 2 })} %</span>
          </li>
          <li className="center">
            <TextMetricIcon />
            <h3>{t("Comments")}</h3>
            <span>{totalComments || 0}</span>
          </li>
          <li className="center">
            <ThumbsUpMetricIcon />
            <h3>Reactions</h3>
            <span>{totalLikes || 0}</span>
          </li>
          <li className="center">
            <PlantMetricIcon />
            <h3>Shares</h3>
            <span>{totalShares || 0}</span>
          </li>
        </ul>
      </div>
    );
  };

  const renderGoogleSearchFeedAdMetrics = () => {
    const channel = "google-search";
    const totalImpressions = getSumByChannelAndMetricField(metrics, channel, "totalImpressions");
    const totalClicks = getSumByChannelAndMetricField(metrics, channel, "totalClicks");
    const totalLikes = getSumByChannelAndMetricField(metrics, channel, "totalLikes");
    const totalShares = getSumByChannelAndMetricField(metrics, channel, "totalShares");
    const totalComments = getSumByChannelAndMetricField(metrics, channel, "totalComments");

    var ctr;

    try {
      ctr = ((totalClicks / totalImpressions) * 100).toFixed(2);
    } catch (error) {
      ctr = 0;
    }

    logger.debug("CampaignRead:renderRedditFeedAdMetrics", "mapped...", {
      totalClicks,
      totalImpressions,
      ctrReal: totalClicks / totalImpressions,
      ctr
    });

    return (
      <div className="campaign-read__social-container">
        <ul>
          <li className="center">
            <EyeMetricIcon />
            <h3>{t("Impressions")}</h3>
            <span>{formatIntlNumber(totalImpressions, { language })}</span>
          </li>
          <li className="center">
            <MouseMetricIcon />
            <h3>{t("Clicks")}</h3>
            <span>{formatIntlNumber(totalClicks, { language })}</span>
          </li>
          <li className="center">
            <PeopleMetricIcon />
            <h3>CTR</h3>
            <span>{formatIntlNumber(ctr, { language, points: 2 })} %</span>
          </li>
          <li className="center">
            <TextMetricIcon />
            <h3>{t("Comments")}</h3>
            <span>{totalComments || 0}%</span>
          </li>
          <li className="center">
            <ThumbsUpMetricIcon />
            <h3>Reactions</h3>
            <span>{totalLikes || 0}</span>
          </li>
          <li className="center">
            <PlantMetricIcon />
            <h3>Shares</h3>
            <span>{totalShares || 0}</span>
          </li>
        </ul>
      </div>
    );
  };

  const renderTikTokMetrics = () => {
    const channel = "tiktok";
    const totalImpressions = getSumByChannelAndMetricField(metrics, channel, "totalImpressions");
    const totalReach = getSumByChannelAndMetricField(metrics, channel, "totalReach");
    const totalLikes = getSumByChannelAndMetricField(metrics, channel, "totalLikes");
    const totalShares = getSumByChannelAndMetricField(metrics, channel, "totalShares");
    const totalComments = getSumByChannelAndMetricField(metrics, channel, "totalComments");
    const totalClicks = getSumByChannelAndMetricField(metrics, channel, "allClicks");
    const totalFollowers = getSumByChannelAndMetricField(metrics, channel, "follows");

    var ctr;

    try {
      ctr = ((totalClicks / totalImpressions) * 100).toFixed(2);
    } catch (error) {
      ctr = 0;
    }

    logger.debug("CampaignRead:renderTikTokMetrics", "mapped...", {
      totalClicks,
      totalImpressions,
      ctrReal: totalClicks / totalImpressions,
      ctr
    });

    return (
      <div className="campaign-read__social-container">
        <ul>
          <li className="center">
            <PeopleMetricIcon />
            <h3>Reichweite</h3>
            <span>{formatIntlNumber(totalReach, { language })}</span>
          </li>
          <li className="center">
            <EyeMetricIcon />
            <h3>{t("Impressions")}</h3>
            <span>{formatIntlNumber(totalImpressions, { language })}</span>
          </li>
          <li className="center">
            <PeopleMetricIcon />
            <h3>{t("Followers")}</h3>
            <span>{formatIntlNumber(totalFollowers, { language })}</span>
          </li>
          <li className="center">
            <MouseMetricIcon />
            <h3>{`Engagement ${t("Clicks")}`}</h3>
            <span>{formatIntlNumber(totalClicks, { language })}</span>
          </li>
          <li className="center">
            <PeopleMetricIcon />
            <h3>CTR</h3>
            <span>{formatIntlNumber(ctr, { language, points: 2 })} %</span>
          </li>
          <li className="center">
            <TextMetricIcon />
            <h3>{t("Comments")}</h3>
            <span>{formatIntlNumber(totalComments, { language })}</span>
          </li>
          <li className="center">
            <ThumbsUpMetricIcon />
            <h3>{t("Reactions")}</h3>
            <span>{formatIntlNumber(totalLikes, { language })}</span>
          </li>
          <li className="center">
            <PlantMetricIcon />
            <h3>{t("Shares")}</h3>
            <span>{formatIntlNumber(totalShares, { language })}</span>
          </li>
        </ul>
      </div>
    );
  };

  const renderSpotifyMetrics = () => {
    const channel = "spotify";
    const totalImpressions = getSumByChannelAndMetricField(metrics, channel, "totalImpressions");
    const totalClicks = getSumByChannelAndMetricField(metrics, channel, "totalClicks");
    const totalReach = getSumByChannelAndMetricField(metrics, channel, "totalReach");
    const completionRate = getSumByChannelAndMetricField(metrics, channel, "completionRate");
    const totalFrequency = getSumByChannelAndMetricFieldFloat(metrics, channel, "frequency");

    var ctr;

    try {
      ctr = ((totalClicks / totalImpressions) * 100).toFixed(2);
    } catch (error) {
      ctr = 0;
    }

    logger.debug("CampaignRead:renderTwitterMetrics", "mapped...", {
      totalClicks,
      totalImpressions,
      ctrReal: totalClicks / totalImpressions,
      ctr
    });

    return (
      <div className="campaign-read__social-container">
        <ul>
          <li className="center">
            <EyeMetricIcon />
            <h3>{t("Impressions")}</h3>
            <span>{formatIntlNumber(totalImpressions, { language })}</span>
          </li>
          <li className="center">
            <MouseMetricIcon />
            <h3>{t("Clicks")}</h3>
            <span>{formatIntlNumber(totalClicks, { language })}</span>
          </li>
          <li className="center">
            <PeopleMetricIcon />
            <h3>CTR</h3>
            <span>{formatIntlNumber(ctr, { language, points: 2 })} %</span>
          </li>
          <li className="center">
            <PlantMetricIcon />
            <h3>Completion Rate</h3>
            <span>{completionRate || 0}%</span>
          </li>
          <li className="center">
            <ThumbsUpMetricIcon />
            <h3>Reach</h3>
            <span>{totalReach || 0}</span>
          </li>
          <li className="center">
            <ThumbsUpMetricIcon />
            <h3>Frequency</h3>
            <span>{totalFrequency || 0}</span>
          </li>
        </ul>
      </div>
    );
  };

  const renderTwitterMetrics = () => {
    const totalImpressions = getSumByChannelAndMetricField(metrics, "twitter", "totalImpressions");
    const totalClicks = getSumByChannelAndMetricField(metrics, "twitter", "totalClicks");
    const totalLikes = getSumByChannelAndMetricField(metrics, "twitter", "totalLikes");
    const totalRetweets = getSumByChannelAndMetricField(metrics, "twitter", "totalRetweets");
    const totalComments = getSumByChannelAndMetricField(metrics, "twitter", "totalComments");

    var ctr;

    try {
      ctr = ((totalClicks / totalImpressions) * 100).toFixed(2);
    } catch (error) {
      ctr = 0;
    }

    logger.debug("CampaignRead:renderTwitterMetrics", "mapped...", {
      totalClicks,
      totalImpressions,
      ctrReal: totalClicks / totalImpressions,
      ctr
    });

    return (
      <div className="campaign-read__social-container">
        <ul>
          <li className="center">
            <EyeMetricIcon />
            <h3>{t("Impressions")}</h3>
            <span>{formatIntlNumber(totalImpressions, { language })}</span>
          </li>
          <li className="center">
            <MouseMetricIcon />
            <h3>{t("Clicks")}</h3>
            <span>{formatIntlNumber(totalClicks, { language })}</span>
          </li>
          <li className="center">
            <PeopleMetricIcon />
            <h3>CTR</h3>
            <span>{formatIntlNumber(ctr, { language, points: 2 })} %</span>
          </li>
          <li className="center">
            <PlantMetricIcon />
            <h3>Retweets</h3>
            <span>{totalRetweets || 0}</span>
          </li>
          <li className="center">
            <ThumbsUpMetricIcon />
            <h3>Favs/Likes</h3>
            <span>{totalLikes || 0}</span>
          </li>
          <li className="center">
            <TextMetricIcon />
            <h3>{t("Comments")}</h3>
            <span>{totalComments || 0}</span>
          </li>
        </ul>
      </div>
    );
  };

  const renderGoogleMetrics = (channelName) => {
    const totalImpressions = getSumByChannelAndMetricField(
      metrics,
      channelName,
      "totalImpressions"
    );
    const totalClicks = getSumByChannelAndMetricField(metrics, channelName, "totalClicks");

    var ctr;

    try {
      ctr = ((totalClicks / totalImpressions) * 100).toFixed(2);
    } catch (error) {
      ctr = 0;
    }

    logger.debug("CampaignRead:renderGoogleMetrics", "mapped...", {
      totalClicks,
      totalImpressions,
      ctrReal: totalClicks / totalImpressions,
      ctr
    });

    return (
      <div className="campaign-read__social-container">
        <ul>
          <li className="center">
            <EyeMetricIcon />
            <h3>{t("Impressions")}</h3>
            <span>{formatIntlNumber(totalImpressions, { language })}</span>
          </li>
          <li className="center">
            <MouseMetricIcon />
            <h3>{t("Clicks")}</h3>
            <span>{formatIntlNumber(totalClicks, { language })}</span>
          </li>
          <li className="center">
            <PeopleMetricIcon />
            <h3>CTR</h3>
            <span>{formatIntlNumber(ctr, { language, points: 2 })} %</span>
          </li>
        </ul>
      </div>
    );
  };

  const renderOtherMetrics = () => {
    return (
      <div className="campaign-read__social-container">
        <ul>
          {[
            { name: "Indeed", channel: "indeed", hasImpressions: true },
            { name: "LinkedIn", channel: "linkedin", hasImpressions: false },
            { name: "Xing", channel: "xing", hasImpressions: false },
            { name: "Google Search", channel: "google-search", hasImpressions: true }
          ]
            .filter((channel) => {
              return channels.includes(channel.channel);
            })
            .map((item, index) => {
              const totalClicks = getSumByChannelAndMetricField(
                metrics,
                item.channel,
                "totalClicks"
              );
              const totalImpressions = getSumByChannelAndMetricField(
                metrics,
                item.channel,
                "totalImpressions"
              );

              return (
                <li className="center" key={index}>
                  <EyeMetricIcon />
                  <h3>{item.name}</h3>
                  <span>
                    {formatIntlNumber(totalClicks, { language })} {t("Clicks")}
                  </span>
                  {item.hasImpressions && (
                    <span style={{ fontSize: 13 }}>{totalImpressions || 0} Impr.</span>
                  )}{" "}
                  <br />
                </li>
              );
            })}
        </ul>
      </div>
    );
  };

  const renderEBMetrics = () => {
    if (!EBMetrics) {
      return <></>;
    }

    const {
      totalReach,
      allClicks,
      totalImpressions,
      totalClicks,
      totalLikes,
      totalShares,
      totalComments
    } = EBMetrics;

    let ctr;
    try {
      ctr = ((totalClicks / totalImpressions) * 100).toFixed(2);
    } catch (error) {
      ctr = 0;
    }

    logger.debug("CampaignRead:renderEBMetrics", "mapped...", {
      totalClicks,
      allClicks,
      totalImpressions,
      ctrReal: totalClicks / totalImpressions,
      ctr
    });

    return (
      <div className="campaign-read__social-container">
        <ul>
          <li className="center">
            <PeopleMetricIcon />
            <h3>Reichweite</h3>
            <span>{formatIntlNumber(totalReach, { language })}</span>
          </li>

          <li className="center">
            <EyeMetricIcon />
            <h3>{t("Impressions")}</h3>
            <span>{formatIntlNumber(totalImpressions, { language })}</span>
          </li>
          <li className="center">
            <MouseMetricIcon />
            <h3>{`Engagement ${t("Clicks")}`}</h3>
            <span>{formatIntlNumber(allClicks, { language })}</span>
          </li>
          <li className="center">
            <MouseMetricIcon />
            <h3>{t("Clicks")}</h3>
            <span>{formatIntlNumber(totalClicks, { language })}</span>
          </li>
          <li className="center">
            <PeopleMetricIcon />
            <h3>CTR</h3>
            <span>{formatIntlNumber(ctr, { language, points: 2 })} %</span>
          </li>
          <li className="center">
            <TextMetricIcon />
            <h3>{t("Comments")}</h3>
            <span>{formatIntlNumber(totalComments, { language })}</span>
          </li>
          <li className="center">
            <ThumbsUpMetricIcon />
            <h3>Reactions</h3>
            <span>{formatIntlNumber(totalLikes, { language })}</span>
          </li>
          <li className="center">
            <PlantMetricIcon />
            <h3>Shares</h3>
            <span>{formatIntlNumber(totalShares, { language })}</span>
          </li>
        </ul>
      </div>
    );
  };

  const intersection = _.intersection(channels, ["linkedin", "indeed", "xing"]);

  return (
    <div>
      {campaignData.employerBrandingId ? (
        <div>
          <h2>Employer Branding</h2>
          <div className="campaign-read__social campaign-read__facebook">
            {renderEBMetrics()}
            <div className="campaign-read__social-preview"></div>
          </div>
        </div>
      ) : null}

      {channels.includes("facebook") && (
        <div>
          <h2>
            <svg className="svg-icon" viewBox="0 0 20 20">
              <path
                fill="none"
                d="M10,0.5c-5.247,0-9.5,4.253-9.5,9.5c0,5.247,4.253,9.5,9.5,9.5c5.247,0,9.5-4.253,9.5-9.5C19.5,4.753,15.247,0.5,10,0.5 M10,18.637c-4.77,0-8.636-3.867-8.636-8.637S5.23,1.364,10,1.364S18.637,5.23,18.637,10S14.77,18.637,10,18.637 M10.858,7.949c0-0.349,0.036-0.536,0.573-0.536h0.719v-1.3H11c-1.38,0-1.866,0.65-1.866,1.743v0.845h-0.86V10h0.86v3.887h1.723V10h1.149l0.152-1.299h-1.302L10.858,7.949z"
              ></path>
            </svg>
            Facebook
          </h2>
          {renderFacebookStats()}
        </div>
      )}
      {channels.includes("instagram") && (
        <div>
          <h2>
            <svg className="svg-icon" viewBox="0 0 20 20">
              <path
                fill="none"
                d="M9.969,0.594c-5.195,0-9.406,4.211-9.406,9.406c0,5.195,4.211,9.406,9.406,9.406c5.195,0,9.406-4.211,9.406-9.406C19.375,4.805,15.164,0.594,9.969,0.594 M9.969,18.552c-4.723,0-8.551-3.829-8.551-8.552s3.829-8.551,8.551-8.551S18.521,5.277,18.521,10S14.691,18.552,9.969,18.552 M12.534,5.724H7.403c-0.944,0-1.71,0.766-1.71,1.71v5.131c0,0.944,0.766,1.71,1.71,1.71h5.131c0.944,0,1.71-0.766,1.71-1.71V7.435C14.244,6.49,13.479,5.724,12.534,5.724M11.679,7.007h1.283V8.29h-1.283V7.007z M9.969,8.29c0.944,0,1.71,0.766,1.71,1.71s-0.766,1.71-1.71,1.71s-1.71-0.766-1.71-1.71S9.024,8.29,9.969,8.29 M13.39,12.565c0,0.472-0.384,0.854-0.855,0.854H7.403c-0.472,0-0.855-0.383-0.855-0.854V9.573h0.898C7.423,9.712,7.403,9.854,7.403,10c0,1.417,1.149,2.565,2.565,2.565c1.416,0,2.565-1.148,2.565-2.565c0-0.146-0.02-0.288-0.043-0.427h0.898V12.565z"
              ></path>
            </svg>
            Instagram
          </h2>
          {renderInstagramStats()}
        </div>
      )}
      {channels.includes("google-display") && (
        <div>
          <h2>Google Display</h2>
          <div className="campaign-read__social campaign-read__facebook">
            {renderGoogleMetrics("google-display")}
            <div className="campaign-read__social-preview"></div>
          </div>
        </div>
      )}
      {channels.includes("youtube") && (
        <div>
          <h2>YouTube</h2>
          <div className="campaign-read__social campaign-read__facebook">
            {renderGoogleMetrics("youtube")}
            <div className="campaign-read__social-preview"></div>
          </div>
        </div>
      )}
      {channels.includes("twitter") && (
        <div>
          <h2>X (ehemals Twitter)</h2>
          <div className="campaign-read__social campaign-read__facebook">
            {renderTwitterMetrics()}
            <div className="campaign-read__social-preview"></div>
          </div>
        </div>
      )}
      {channels.includes("spotify") && (
        <div>
          <h2>Spotify</h2>
          <div className="campaign-read__social campaign-read__facebook">
            {renderSpotifyMetrics()}
            <div className="campaign-read__social-preview"></div>
          </div>
        </div>
      )}
      {channels.includes("tiktok") && (
        <div>
          <h2>TikTok</h2>
          <div className="campaign-read__social campaign-read__facebook">
            {renderTikTokMetrics()}
            <div className="campaign-read__social-preview"></div>
          </div>
        </div>
      )}
      {channels.includes("linkedin-feed") && (
        <div>
          <h2>LinkedIn Feed-Anzeige</h2>
          <div className="campaign-read__social campaign-read__facebook">
            {renderLinkedInFeedAdMetrics()}
            <div className="campaign-read__social-preview"></div>
          </div>
        </div>
      )}
      {channels.includes("pinterest") && (
        <div>
          <h2>Pinterest</h2>
          <div className="campaign-read__social campaign-read__facebook">
            {renderPinterestFeedAdMetrics()}
            <div className="campaign-read__social-preview"></div>
          </div>
        </div>
      )}
      {channels.includes("ebay-kleinanzeigen") && (
        <div>
          <h2>Kleinanzeigen</h2>
          <div className="campaign-read__social campaign-read__facebook">
            {renderEbayKleinanzeigenFeedAdMetrics()}
            <div className="campaign-read__social-preview"></div>
          </div>
        </div>
      )}
      {channels.includes("reddit") && (
        <div>
          <h2>Reddit</h2>
          <div className="campaign-read__social campaign-read__facebook">
            {renderRedditFeedAdMetrics()}
            <div className="campaign-read__social-preview"></div>
          </div>
        </div>
      )}
      {channels.includes("snapchat") && (
        <div>
          <h2>Snapchat</h2>
          <div className="campaign-read__social campaign-read__facebook">
            {renderSnapchatFeedAdMetrics()}
            <div className="campaign-read__social-preview"></div>
          </div>
        </div>
      )}
      {channels.includes("google-search") && (
        <div>
          <h2>Google Search</h2>
          <div className="campaign-read__social campaign-read__facebook">
            {renderGoogleSearchFeedAdMetrics()}
            <div className="campaign-read__social-preview"></div>
          </div>
        </div>
      )}

      {intersection.length > 0 && (
        <div>
          <h2>{t("Other channels")}</h2>
          <div className="campaign-read__social campaign-read__facebook">
            {renderOtherMetrics()}
            <div className="campaign-read__social-preview"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignReadStats;
